// Realistic Medical Body Diagram Component (extracted from Recommendations.js)
export const BodyDiagram = ({ affectedAreas = [], gender = "Male" }) => {
  const isActive = (area) => affectedAreas.some(a => a.toLowerCase().includes(area));
  
  // Select appropriate skeleton image based on gender
  const skeletonImage = gender === "Female" 
    ? "https://images.unsplash.com/photo-1530210124550-912dc1381cb8?w=400&q=85" // Female skeleton
    : "https://images.unsplash.com/photo-1714938944803-7589601c962b?w=400&q=85"; // Male skeleton
  
  return (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* Medical-grade anatomical diagram as base */}
      <div className="relative rounded-xl overflow-hidden border-2 border-gray-200 bg-white p-2">
        <img 
          src={skeletonImage}
          alt={`${gender} anatomical body diagram`}
          className="w-full h-auto opacity-95"
        />
        
        {/* Interactive overlay for highlighting affected areas */}
        <svg viewBox="0 0 200 520" className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          {/* Glow effect for active areas */}
          <filter id="activeGlow">
            <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Highlight overlays for affected body systems - positioned over anatomical diagram */}
        
        {/* Head/Brain area */}
        {(isActive('nervous') || isActive('brain')) && (
          <ellipse 
            cx="100" cy="50" rx="35" ry="40" 
            fill="#10b981" 
            opacity="0.4"
            filter="url(#activeGlow)"
            className="animate-pulse"
          >
            <title>Brain & Nervous System Targeted</title>
          </ellipse>
        )}
        
        {/* Chest/Heart/Respiratory area */}
        {(isActive('respiratory') || isActive('cardiovascular') || isActive('heart')) && (
          <ellipse 
            cx="100" cy="150" rx="50" ry="55"
            fill="#10b981" 
            opacity="0.4"
            filter="url(#activeGlow)"
            className="animate-pulse"
          >
            <title>Chest, Heart & Respiratory Targeted</title>
          </ellipse>
        )}
        
        {/* Abdomen/Digestive area */}
        {(isActive('digestive') || isActive('liver') || isActive('kidney')) && (
          <ellipse 
            cx="100" cy="270" rx="52" ry="65"
            fill="#10b981" 
            opacity="0.4"
            filter="url(#activeGlow)"
            className="animate-pulse"
          >
            <title>Digestive System Targeted</title>
          </ellipse>
        )}
        
        {/* Legs/Joints area */}
        {(isActive('joint') || isActive('musculoskeletal')) && (
          <>
            <ellipse 
              cx="75" cy="400" rx="22" ry="80"
              fill="#10b981" 
              opacity="0.4"
              filter="url(#activeGlow)"
              className="animate-pulse"
            />
            <ellipse 
              cx="125" cy="400" rx="22" ry="80"
              fill="#10b981" 
              opacity="0.4"
              filter="url(#activeGlow)"
              className="animate-pulse"
            />
          </>
        )}
        
        {/* Immune system (full body) */}
        {isActive('immune') && (
          <>
            <circle cx="70" cy="120" r="8" fill="#8b5cf6" opacity="0.7" filter="url(#activeGlow)" className="animate-pulse"/>
            <circle cx="130" cy="120" r="8" fill="#8b5cf6" opacity="0.7" filter="url(#activeGlow)" className="animate-pulse"/>
            <circle cx="80" cy="200" r="8" fill="#8b5cf6" opacity="0.7" filter="url(#activeGlow)" className="animate-pulse"/>
            <circle cx="120" cy="200" r="8" fill="#8b5cf6" opacity="0.7" filter="url(#activeGlow)" className="animate-pulse"/>
            <circle cx="90" cy="280" r="8" fill="#8b5cf6" opacity="0.7" filter="url(#activeGlow)" className="animate-pulse"/>
            <circle cx="110" cy="280" r="8" fill="#8b5cf6" opacity="0.7" filter="url(#activeGlow)" className="animate-pulse"/>
          </>
        )}
      </svg>
      </div>
      
      {/* Legend */}
      <div className="mt-4 space-y-2 bg-white rounded-lg p-3 border border-gray-200">
        <p className="text-xs font-semibold text-gray-700 mb-2">Body Systems</p>
        <div className="flex items-center text-xs">
          <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2 shadow-sm"></div>
          <span className="text-gray-600">Targeted by herbs</span>
        </div>
        <div className="flex items-center text-xs">
          <div className="w-3 h-3 bg-gray-300 rounded-full mr-2"></div>
          <span className="text-gray-500">Not targeted</span>
        </div>
      </div>
    </div>
  );
};
